import { useNavigate, useLocation } from 'react-router-dom';
import logoText from "../../assets/clapkartel-Icon-updated.png";
import SearchIcon from "../../assets/searchVector.png";
import { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getCurrentUserId } from '../../utils/auth';
import {
  Menu,
  X,
  Film,
  BookOpen,
  Key,
  Folder,
  Briefcase,
  Grid,
  Users,
  UserPlus,
  MessageSquare,
  Bell,
  Settings,
  User as UserIcon,
  LogOut
} from 'lucide-react';
import "./index.css";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId) return;

    const notifRef = collection(db, 'notifications', userId, 'user_notifications');
    const unreadQuery = query(notifRef, where('isRead', '==', false));

    const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
      setUnreadCount(snapshot.docs.length);
    });

    return () => unsubscribe();
  }, []);

  // Close drawer on page navigation
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  /**
   * Handle user logout
   * Clears authentication token and redirects to login page
   */
  const handleLogout = () => {
    // Fire Firebase signOut in background (no await to prevent hanging UI)
    try {
      signOut(auth);
    } catch (_) { }

    // Clear all storage mechanisms to prevent session leakage
    localStorage.clear();
    sessionStorage.clear();

    // Force a full browser reload/replace on the current tab to destroy react memory
    window.location.replace(window.location.origin + '/login');
  };

  /**
   * Handle search - Navigate to users page with search query
   */
  const handleSearch = () => {
    navigate('/users', {
      state: { searchQuery: searchQuery.trim() }
    });
  };

  /**
   * Handle Enter key press in search input
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const menuItems = [
    { label: 'Cast & Crew', path: '/cast-crew', icon: Film },
    {
      label: 'Lessons',
      path: '/other-section-detail',
      icon: BookOpen,
      state: { sectionId: '181', sectionName: 'Lessons', sectionImage: 'dailyNews.png' }
    },
    { label: 'Rentals', path: '/', icon: Key, state: { scrollToRentals: true } },
    { label: 'Directory', path: '/directory', icon: Folder },
    { label: '24 Crafts', path: '/craft', icon: Briefcase },
    { label: 'Other Sections', path: '/other-section', icon: Grid },
    { label: 'Users', path: '/users', icon: Users },
    { label: 'Followers', path: '/followers', icon: UserPlus },
    { label: 'Messages', path: '/messages', icon: MessageSquare },
    { label: 'Notifications', path: '/notification', icon: Bell, showBadge: true },
    { label: 'Settings', path: '/settings', icon: Settings },
    { label: 'Profile', path: '/profile', icon: UserIcon }
  ];

  const handleMenuItemClick = (item) => {
    setIsDrawerOpen(false);
    if (item.state) {
      navigate(item.path, { state: item.state });
    } else {
      navigate(item.path);
    }
  };

  const isItemActive = (item) => {
    if (item.path === '/') {
      return location.pathname === '/' && location.state?.scrollToRentals;
    }
    return location.pathname === item.path;
  };

  return (
    <div className="header-container">
      <img
        src={logoText}
        alt="logoText"
        className="header-logo"
        onClick={() => navigate('/')}
      />
      <div className="header-search-wrapper">
        <input
          type="text"
          className="header-search-input"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <img
          src={SearchIcon}
          alt="searchVector"
          className="header-search-icon"
          onClick={handleSearch}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <button
        className="header-logout-button"
        onClick={handleLogout}
      >
        Logout
      </button>

      {/* Hamburger button for mobile/tablet */}
      <button
        className="header-hamburger-button"
        onClick={() => setIsDrawerOpen(true)}
        aria-label="Open Navigation Drawer"
      >
        <Menu size={26} color="#FFDC26" />
      </button>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && (
        <div
          className="mobile-drawer-overlay"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Mobile Drawer Panel */}
      <div className={`mobile-drawer-panel ${isDrawerOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <img
            src={logoText}
            alt="logoText"
            className="drawer-logo"
            onClick={() => {
              setIsDrawerOpen(false);
              navigate('/');
            }}
          />
          <button
            className="drawer-close-button"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Close Navigation Drawer"
          >
            <X size={26} color="#FFDC26" />
          </button>
        </div>

        <div className="mobile-drawer-content">
          <ul className="mobile-drawer-menu">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const activeClass = isItemActive(item) ? 'active' : '';
              return (
                <li
                  key={item.label}
                  className={`mobile-drawer-item ${activeClass}`}
                  onClick={() => handleMenuItemClick(item)}
                >
                  <IconComponent size={20} className="drawer-item-icon" />
                  <span className="drawer-item-label">{item.label}</span>
                  {item.showBadge && unreadCount > 0 && (
                    <span className="drawer-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mobile-drawer-footer">
          <button
            className="drawer-logout-button"
            onClick={handleLogout}
          >
            <LogOut size={18} style={{ marginRight: '8px' }} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;